import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import * as moment from 'moment-timezone';

@Injectable({
	providedIn: 'root',
})
export abstract class BaseHttpService {
	protected readonly baseUrl = environment.baseURL;

	constructor(protected readonly http: HttpClient) {}

	protected get<T = any>(urlPath: string, queryParams?: Record<string, any>, headers?: HttpHeaders | { [header: string]: string | string[] }): Observable<T> {
		const params = queryParams ? this.buildQueryParams(queryParams) : undefined;
		const mergedHeaders = this.mergeHeaders(headers);
		return this.http.get<T>(`${this.baseUrl}/${urlPath}`, {
			params,
			headers: mergedHeaders,
		});
	}

	protected post<T = any>(urlPath: string, data: unknown, headers?: HttpHeaders | { [header: string]: string | string[] }): Observable<T> {
		const mergedHeaders = this.mergeHeaders(headers);
		return this.http.post<T>(`${this.baseUrl}/${urlPath}`, data, { headers: mergedHeaders });
	}

	protected put<T = any>(urlPath: string, data: unknown, headers?: HttpHeaders | { [header: string]: string | string[] }): Observable<T> {
		const mergedHeaders = this.mergeHeaders(headers);
		return this.http.put<T>(`${this.baseUrl}/${urlPath}`, data, { headers: mergedHeaders });
	}

	protected delete<T = any>(urlPath: string, headers?: HttpHeaders | { [header: string]: string | string[] }): Observable<T> {
		const mergedHeaders = this.mergeHeaders(headers);
		return this.http.delete<T>(`${this.baseUrl}/${urlPath}`, { headers: mergedHeaders });
	}

	protected patch<T = any>(urlPath: string, data: unknown, headers?: HttpHeaders | { [header: string]: string | string[] }): Observable<T> {
		const mergedHeaders = this.mergeHeaders(headers);
		return this.http.patch<T>(`${this.baseUrl}/${urlPath}`, data, { headers: mergedHeaders });
	}

	protected buildQueryParams(params: Record<string, any>): HttpParams {
		let httpParams = new HttpParams();

		Object.keys(params).forEach((key) => {
			const value = params[key];
			if (value !== null && value !== undefined && value !== '') {
				httpParams = httpParams.set(key, value);
			}
		});

		return httpParams;
	}

	private getDefaultHeaders(): { [header: string]: string } {
		const currentTimezone = moment.tz.guess() || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid';
		return {
			timezone: currentTimezone,
			'accept-language': 'en',
			platform: 'web',
			app: 'dashboard',
		};
	}

	private mergeHeaders(customHeaders?: HttpHeaders | { [header: string]: string | string[] }): HttpHeaders {
		const defaultHeaders = this.getDefaultHeaders();

		if (!customHeaders) {
			return new HttpHeaders(defaultHeaders);
		}

		if (customHeaders instanceof HttpHeaders) {
			let mergedHeaders = new HttpHeaders(defaultHeaders);
			customHeaders.keys().forEach((key) => {
				mergedHeaders = mergedHeaders.set(key, customHeaders.get(key) || '');
			});
			return mergedHeaders;
		}

		return new HttpHeaders({ ...defaultHeaders, ...customHeaders });
	}
}
